import { Toast } from '@modusign/design-system';
import { useUpdateTemplate } from '@modusign/openapi-client-template';
import { UpdateTemplateRequestDto } from '@modusign/openapi-client-template/model';
import { useSigners } from '@modusign/react-signing';
import { useSigningState } from '@modusign/react-signing/setup';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import AttachmentExceededAlertModal from '@app/containers/Modal/AttachmentExceededAlertModal';
import { toDocumentModelForSetup } from '@app/features/apply-react-signing/libs/signing-mapper';
import { templateMutationErrorHandler } from '@app/features/apply-react-signing/libs/template/errorHandler';
import ViewModeInlineAlert from '@app/features/template/components/TemplateEditPages/ViewModeInlineAlert';
import TemplateExitModal from '@app/features/template/components/TemplateExitModal';
import { TemplateSignerSetupStep } from '@app/features/template/components/TemplateSignerSetupStep';
import useTemplateOrdering from '@app/features/template/hooks/useTemplateOrdering';
import useTemplatePermission from '@app/features/template/hooks/useTemplatePermission';
import { usePDF } from '@app/hooks/usePDF';
import useUsageCount from '@app/hooks/useUsageCount';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { pdfToBase64 } from '@app/libs/pdf/pdfToBase64';
import { templateLoading, templateLoadingFinish } from '@app/store/template/actions';
import { fileIdSelector, idSelector, labelIdsSelector } from '@app/store/template/selectors';
import { postDocumentUpload } from '@app/store/transport-layer/upload/document/upload';

interface TemplateParticipantEditPageProps {
  handleExitClick: () => void;
  handleNextRouteClick: () => void;
}

type ExitStatus = 'idle' | 'save' | 'unsave';

function TemplateParticipantEditPage({
  handleExitClick,
  handleNextRouteClick,
}: TemplateParticipantEditPageProps) {
  const dispatch = useDispatch();
  const { signingState } = useSigningState();
  const { initOrdering } = useTemplateOrdering();
  const { signers } = useSigners();
  const workspaceId = useWorkspaceId();
  const { id } = useParams<{ id: string }>();
  const { editTemplatePermission, editSharedTemplatePermission } = useTemplatePermission({
    resourceRoleQueryOption: {
      templateId: id,
      options: {
        enabled: !!workspaceId,
      },
    },
  });
  const { templateCount, attachmentRequestCount } = useUsageCount();

  const hasAttachmentLimitExceeded = attachmentRequestCount.limit < signingState.attachments.length;

  const fileId = useSelector(fileIdSelector);
  const templateId = useSelector(idSelector);
  const labelIds = useSelector(labelIdsSelector);

  const [exitStatus, setExitStatus] = useState<ExitStatus>('idle');
  const [isAttachmentExceededAlertModalOpen, setAttachmentExceededAlertModalOpen] = useState(false);
  const { pdf } = usePDF();

  const { mutate: updateTemplate } = useUpdateTemplate({
    mutation: {
      onSuccess: () => {
        initOrdering();
        dispatch(templateLoadingFinish());
        Toast.success({ description: '템플릿을 수정했습니다.' });
        handleExitClick();
      },
      onError: (error, { data }) => {
        dispatch(templateLoadingFinish());
        templateMutationErrorHandler(dispatch, error, data, templateCount.limit);
      },
    },
  });

  const getIsAllRoleValid = () => {
    return signers.length > 0 && signers.every((v) => !!v.role);
  };

  const handleBackStepClick = () => {
    if (!editTemplatePermission.isPermitted) {
      handleExitClick();
      return;
    }
    if (getIsAllRoleValid()) {
      setExitStatus('save');
    } else {
      setExitStatus('unsave');
    }
  };

  const handleSaveExitClick = async () => {
    setExitStatus('idle');
    if (hasAttachmentLimitExceeded) {
      setAttachmentExceededAlertModalOpen(true);
      return;
    }
    dispatch(templateLoading());

    let newFileId = fileId;
    const fileChanged = fileId && pdf;
    if (fileChanged) {
      const pdfAsBase64 = await pdfToBase64(pdf);
      const { fileId: id } = await postDocumentUpload({ base64: pdfAsBase64 });
      newFileId = id;
    }

    const data = toDocumentModelForSetup(signingState);
    updateTemplate({
      id: templateId,
      workspaceId,
      data: {
        attachments: data.attachments,
        participants: data.participants,
        fieldGroups: data.fieldGroups,
        fields: data.fields,
        title: signingState.document.title,
        file: { id: newFileId },
        verifications: data.verifications,
        labelIds,
      } as UpdateTemplateRequestDto,
    });
  };

  const handleCloseButtonClick = () => {
    setExitStatus('idle');
  };

  return (
    <>
      <TemplateSignerSetupStep
        editTemplatePermission={editTemplatePermission.isPermitted}
        isEdition
        onBackStep={handleBackStepClick}
        onNextStep={handleNextRouteClick}
      />
      {isAttachmentExceededAlertModalOpen && (
        <AttachmentExceededAlertModal
          onConfirm={() => {
            setAttachmentExceededAlertModalOpen(false);
          }}
        />
      )}
      <TemplateExitModal
        exitStatus={exitStatus}
        onCloseButtonClick={handleCloseButtonClick}
        onSaveExitClick={handleSaveExitClick}
        onUnsaveExitClick={handleExitClick}
      />
      {!editTemplatePermission.isPermitted && !editSharedTemplatePermission && (
        <ViewModeInlineAlert />
      )}
    </>
  );
}

export default TemplateParticipantEditPage;
